// Location: Corporate-dashboard/Container/components/dashboard/BarChartDisplay.js

import React, { useState, useEffect } from 'react';
import { Row, Col, Typography, Space, Button, Empty } from 'antd';
import { Bar } from 'react-chartjs-2';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { layouts } from 'chart.js';

const { Text, Title } = Typography;

const BarChartDisplay = ({ barChartRef, barOptions, barData, legendItems, onBarClick, startIndex, isExporting, totalItems, itemsPerPage, onPrevious, onNext, selectedFilter, allDetailsData, onClearSelection, selectedDegree, containerRef }) => {
  // --- New: State for selected bar and its details ---
  const [selectedData, setSelectedData] = useState({
    degree: '',
    floated: 0,
    active: 0,
    closed: 0,
  });

  // Sync internal state with parent's selectedDegree
  useEffect(() => {
    if (!selectedDegree) {
      setSelectedData({
        degree: '',
        floated: 0,
        active: 0,
        closed: 0,
      });
    }
  }, [selectedDegree]);

  // Dynamic title based on selected filter
  const getDynamicTitle = () => {
    const filter = selectedFilter || 'JOBS'; // Default to INTERNSHIP if undefined
    const filterTitles = {
      JOBS: 'No. of Roles by Degree',
      INTERNSHIP: 'No. of Internship roles by Degree',
      APPRENTICESHIP: 'No. of Apprenticeship roles by Degree',
      PROJECTS: 'No. of Projects by Degree',
      INTERNSHIP_PPO: 'No. of Internship+PPO roles by Degree'
    };
    return filterTitles[filter] || 'No. of Roles by Degree';
  };

  // Check if there's any data for the bar chart
  const hasBarData = barData && barData.datasets && barData.datasets.length > 0 && 
    barData.datasets[0].data && barData.datasets[0].data.some(value => value > 0);

  // --- Handle bar click ---
 
  const handleBarClick = (event, elements) => {
    if (!elements.length) return;

    const element = elements[0];
    const dataIndex = element.index;
    
    const actualIndex = startIndex + dataIndex;
    const degreeData = allDetailsData[actualIndex];

    if (degreeData) {
      setSelectedData({
        degree: degreeData.degree,
        floated: degreeData.floated,
        active: degreeData.active,
        closed: degreeData.closed,
      });
    }

    // Call parent's click handler to update gender chart
    if (onBarClick) {
      onBarClick(event, elements);
    }
  };

    
  

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {!isExporting && (
        <Title level={4} style={{ marginBottom: 20, flexShrink: 0 }}>
          {getDynamicTitle()}
        </Title>
      )}

      {hasBarData ? (
        <>
          {/* Bar Chart Container */}
          <div
            ref={containerRef}
            style={{
              flexGrow: 1,
              minHeight: 300,
              marginBottom: '20px',
              width: '100%',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <div style={{ width: '100%', height: '100%' }}>
              <Bar
                ref={barChartRef}
                options={{
                  ...barOptions,
                  onClick: (event, elements) => handleBarClick(event, elements),
                  responsive: true,
                  maintainAspectRatio: false,
                  onHover: (event, activeElements) => {
                    event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
                  },
                  plugins: {
                    ...barOptions.plugins,
                  datalabels: {
                      display: false,
                    },
                  },
                }}
                data={barData}
              />
            </div>
          </div>

      {/* Degree Label with Scroll Buttons */}
      {!isExporting && (
      <div style={{ 
        width: '100%', 
        marginBottom: 20, 
        display: 'flex', 
        justifyContent: 'flex-end', 
        alignItems: 'center',
        flexShrink: 0 
      }}>
        <Text strong style={{ fontSize: 14, color: '#262626' }}>Degree</Text>
        
        {/* thin gray line */}
        <div
          style={{
            flexGrow: 0.89,
            height: 1,
            backgroundColor: '#d1d1d1',
            borderRadius: 1,
            marginRight: 12,
            marginLeft: 12,
          }}
        ></div>
        
        {/* Scroll Buttons */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px'
        }}>
          <Button
            type="text"
            icon={<LeftOutlined />}
            size="small"
            onClick={onPrevious}
            disabled={startIndex === 0}
            style={{ 
              backgroundColor: '#fff',
              borderRadius: '6px',
              border: '1px solid #d9d9d9',
              padding: '4px',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'none',
              color: startIndex === 0 ? '#bfbfbf' : 'rgba(50, 73, 215, 1)',
            }}
          />
          <Button
            type="text"
            icon={<RightOutlined />}
            size="small"
            onClick={onNext}
            disabled={startIndex + itemsPerPage >= totalItems}
            style={{ 
              backgroundColor: '#fff',
              borderRadius: '6px',
              border: '1px solid #d9d9d9',
              padding: '4px',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'none',
              color: startIndex + itemsPerPage >= totalItems ? '#bfbfbf' : 'rgba(50, 73, 215, 1)',
            }}
          />
        </div>
      </div>
      )}

      {/* Legend */}
      <div style={{ width: '100%', marginBottom: 20, flexShrink: 0 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space size="large">
              {legendItems.map((item) => (
                <Space key={item.label} size={8}>
                  <div
                    style={{
                      width: 14,
                      height: 14,
                      backgroundColor: item.color,
                      borderRadius: '50%',
                    }}
                  />
                  <Text>{item.label}</Text>
                </Space>
              ))}
            </Space>
          </Col>
        </Row>
      </div>
      
      {/* Clear button above details box */}
      {onClearSelection && !isExporting && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
          <Text
            style={{
              fontSize: '11px',
              color: (selectedData.degree || selectedDegree) ? 'red' : 'grey',
              cursor: 'pointer'
            }}
            onClick={onClearSelection}
          >
            Clear
          </Text>
        </div>
      )}

      {/* Selected Details Box */}
      {!isExporting && (
        <div
          style={{
            width: '100%',
            borderRadius: '8px',
            padding: '12px 16px',
            border: selectedData.degree ? '1px solid #435fe8' : '1px solid rgba(194, 194, 194, 1)',
            flexShrink: 0,
          }}
        >
          {selectedData.degree ? (
            <Space wrap size={[24, 8]} style={{ width: '100%' }}>
              <Text strong>
                Selected <span style={{ color: 'rgba(50, 73, 215, 1)' }}>{selectedData.degree}</span>
              </Text>
              <Space size={4}>
                <div
                  style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#001F54' }}
                />
                <Text strong>{selectedData.floated}</Text> <Text type="secondary">roles</Text>
              </Space>
              <Space size={4}>
                <div
                  style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#ADE8F4' }}
                />
                <Text strong>{selectedData.active}</Text> <Text type="secondary">roles</Text>
              </Space>
              <Space size={4}>
                <div
                  style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#D9D9D9' }}
                />
                <Text strong>{selectedData.closed}</Text> <Text type="secondary">roles</Text>
              </Space>
            </Space>
          ) : (
            <Space size={8}>
              <svg width="18" height="21" viewBox="0 0 18 21" style={{ marginLeft: '2px' }}>
                {/* Line for exclamation mark */}
                <rect 
                  x="10.05" 
                  y="3" 
                  width="4.022172927856445" 
                  height="12" 
                  fill="rgba(50, 73, 215, 1)"
                  opacity="1"
                />
                {/* Dot for exclamation mark */}
                <rect 
                  x="10.05" 
                  y="17" 
                  width="4.022172927856445" 
                  height="4" 
                  fill="rgba(50, 73, 215, 1)"
                  opacity="1"
                />
              </svg>
              <Text style={{ 
                color: '#262626',
                fontFamily: 'Plus Jakarta Sans',
                fontWeight: 400,
                fontSize: 14,
                lineHeight: '18px',
                letterSpacing: '0%'
              }}>
                Note: Click the graph for more details.
              </Text>
            </Space>
          )}
        </div>
      )}
        </>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', flexGrow: 1 }}>
          <Empty />
        </div>
      )}
    </div>
  );
};


// Hide datalabels by default
export const barOptions = {
  maintainAspectRatio: false,
  layout:{
    padding:{
      left: 0,
      right: 0
    }
  },
  plugins: {
    legend: { display: false },
    datalabels: { display: false },
  },
};


export default BarChartDisplay;
